import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import PostAddIcon from "@mui/icons-material/PostAdd";
import CalculateIcon from "@mui/icons-material/Calculate";
import AddMakeModel from "./AddMakeModel";
import IdvCalculatorDialog from "./IdvCalculatorDialog";
import AddMakeModelDrawer from "../../User/AddMakeModelDrawer";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
import ConfirmBox from "../../../UiComponents/ConfirmBox/ConfirmBox";
import {
  EditIcon,
  AddIcon,
  SearchIcon,
  NotInterestedIcon,
  DoneOutlineIcon,
} from "../../../Resources/Icons/icons";
import {
  GetMakeModel,
  GetUserFindbyId,
  UpdateUser,
  UpdateMakeModel,
  FilterMakeModel,
} from "../../../Service/_index";
import Dialog from "@mui/material/Dialog";
import { Transition } from "../../../UiComponents/Transition/Transition";
import { Divider } from "@mui/material";
import TabFilter from "../../../Components/Tabs/Tabs";
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import DisabledImage from "../../../../src/Resources/Images/disable.png";
import ActiveImage from "../../../../src/Resources/Images/ActiveImage.png";
import moment from "moment";

const FilterOption = [
  {
    label: "All Data",
    isEnabled: "all",
    value: 1,
  },
  {
    label: "ActiveData",
    isEnabled: "true",
    value: 2,
  },
  {
    label: "In ActiveData",
    isEnabled: "false",
    value: 3,
  },
];

const getClassName = (data) => {
  return data.status == "Active"
    ? "Active-chip"
    : data.status == "Inactive"
      ? " inActive-chip"
      : "";
};

const headCells = [
  {
    id: "make",
    placeMent: true,
    disablePadding: false,
    label: "Make",
  },
  {
    id: "companyLabel",
    placeMent: true,
    disablePadding: false,
    label: "Company",
  },
  {
    id: "model",
    placeMent: true,
    disablePadding: false,
    label: "Model",
  },
  {
    id: "idvRangeFrom",
    placeMent: true,
    disablePadding: false,
    label: "IDV Range From",
  },
  {
    id: "idvRangeTo",
    placeMent: true,
    disablePadding: false,
    label: "IDV Range To",
  },
  {
    id: "cc",
    placeMent: true,
    disablePadding: false,
    label: "CC",
  },
  {
    id: "discount",
    placeMent: true,
    disablePadding: false,
    label: "Discount (%)",
  },
  {
    id: "vehicleVariant",
    placeMent: true,
    disablePadding: false,
    label: "Variant",
  },
  {
    id: "createdAt",
    placeMent: true,
    disablePadding: false,
    label: "Created Date",
  },
  // {
  //   id: "updatedAt",
  //   placeMent: true,
  //   disablePadding: false,
  //   label: "Updated Date",
  // },
  {
    id: "status",
    placeMent: true,
    disablePadding: false,
    label: "Status",
    class: getClassName,
  },
  {
    id: "idvCalculator",
    placeMent: true,
    disablePadding: false,
    label: "IDV",
  },
  {
    id: "createPolicy",
    placeMent: true,
    disablePadding: false,
    label: "Create Policy",
  },
];

const MakeModel = () => {
  const navigate = useNavigate();
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("id");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [selectedData, setSelectedData] = React.useState({});
  const [openMakeModelDrawer, setOpenMakeModelDrawer] = React.useState(false);

  // ── Assigned-only view for non-admin users ────────────────────────────────
  // A CLIENT (admin) manages the shared master. Every other role sees ONLY the
  // make/models assigned to them (User.makemodelids) and may change nothing but
  // their OWN discount — editing the master's value here would reprice every
  // other user's policies too.
  const loggedInUserType = sessionStorage.getItem("userType");
  const isMasterAdmin = String(loggedInUserType || "").toUpperCase() === "CLIENT";
  const [userMakeModelRows, setUserMakeModelRows] = React.useState(null); // null = not loaded
  const [openDiscountDialog, setOpenDiscountDialog] = React.useState(false);
  const [discountRow, setDiscountRow] = React.useState(null);

  const loadUserAssignments = React.useCallback(() => {
    const userId = sessionStorage.getItem("UserId");
    if (isMasterAdmin || !userId) {
      setUserMakeModelRows([]);
      return Promise.resolve([]);
    }
    return GetUserFindbyId(userId)
      .then((res) => {
        const rows = res?.data?.makemodelids || [];
        setUserMakeModelRows(rows);
        return rows;
      })
      .catch(() => {
        setUserMakeModelRows([]);
        return [];
      });
  }, [isMasterAdmin]);

  React.useEffect(() => {
    loadUserAssignments();
  }, [loadUserAssignments]);

  // Restrict the list only once we know the user actually has assignments.
  const assignedOnly = !isMasterAdmin && (userMakeModelRows?.length || 0) > 0;

  // IDV calculator. Holds the row it was opened from, or {} for the standalone
  // calculator in the header — null means closed.
  const [idvCalcRow, setIdvCalcRow] = React.useState(null);

  const [openConfirmBox, setOpenConfirmBox] = React.useState(false);
  const [makeModel, setMakeModel] = React.useState([]);
  const [searchText, setSearchText] = useState("");
  const [activeBoolean, setActiveBoolean] = useState();
  const [pageView, setPageView] = useState(2);
  const [loading, setLoading] = useState(false);
  const [tableConfig, setTableConfig] = useState({
    isAction: true,
    isEdit: true,
    isSearch: true,
    isSwitch: true,
    historyEntity: "makeModel",
  });
  const [selectedTab, setSelectedTab] = useState(1);
  const [filter, setFilter] = useState({
    fn: (items) => {
      return items;
    },
  });

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    const selectedOption = FilterOption.find(
      (option) => option.value === newValue
    );
    FilterMakeModelFunction(selectedOption);
  };

  // Navigate to Online Policy create page with this make/model pre-filled
  const CreatePolicyFromRow = (row) => {
    navigate("/onlinePolicy", {
      state: {
        makeModelPrefill: {
          _id: row._id,
          make: row.make,
          model: row.model,
          idvRangeFrom: row.idvRangeFrom,
          idvRangeTo: row.idvRangeTo,
          cc: row.cc,
          company: row.company,
          discount: row.discount,
          // The IDV range is derived from this when present — without it the
          // policy form falls back to the stored range and can auto-fill an
          // IDV outside its own validation bounds.
          manufacturerSellingPrice: row.manufacturerSellingPrice,
          // Without this the policy would fall back to the formula's default
          // depreciation and derive a different range from the same price.
          depreciationPercent: row.depreciationPercent,
          modelNotFound: !!row.modelNotFound,
          // Drive the policy's Zero Depreciation and TPPD behaviour.
          nilDipPrice: row.nilDipPrice,
          tppdRequired: !!row.tppdRequired,
          paCoverMinAmount: row.paCoverMinAmount,
          vehicleVariant:
            row.vehicleVariant && row.vehicleVariant !== "-"
              ? row.vehicleVariant
              : "",
        },
      },
    });
  };

  const FilteredData = makeModel
    ?.filter((el) => {
      return `${el?.make},${el?.model},${el?.idvRangeFrom},${el?.idvRangeTo},${el?.cc},${el?.company},${el?.discount}`
        .toLowerCase()
        .includes(searchText.toLowerCase());
    })
    .map((row) => ({
      ...row,
      // The table column; `row.company` itself stays untouched for edit mode
      // (EditMakeModelData) and CreatePolicyFromRow.
      companyLabel: row.company,
      // Check this vehicle's ex-showroom price against its saved IDV range, or
      // calculate a range for any price by hand. An icon rather than a labelled
      // button — unlike Create Policy this stays on the page.
      idvCalculator: isMasterAdmin ? (
        <Tooltip title="IDV Calculator — ex-showroom price to IDV range">
          <IconButton
            size="small"
            onClick={() => setIdvCalcRow(row)}
            sx={{
              color: "#1976d2",
              "&:hover": { backgroundColor: "#e3f2fd" },
            }}
          >
            <CalculateIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null,
      // A labelled button, not a bare icon: this is the one action in the row
      // that leaves the page entirely, so it should say what it does rather
      // than make the user hover to find out.
      createPolicy: (
        <Tooltip
          title={
            row.isEnabled
              ? `Start an Online Policy for ${row.make} ${row.model}`
              : "Activate this make & model before creating a policy"
          }
        >
          {/* A disabled MUI Button fires no pointer events, so the Tooltip
              needs a plain wrapper to listen on — without the span the
              "why is this off?" hint never shows on inactive rows. */}
          <span>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={!row.isEnabled}
              startIcon={<PostAddIcon sx={{ fontSize: "16px !important" }} />}
              onClick={() => CreatePolicyFromRow(row)}
              sx={{
                textTransform: "none",
                whiteSpace: "nowrap",
                fontSize: "12px",
                fontWeight: 600,
                px: 1.5,
                py: 0.6,
                borderRadius: "6px",
                backgroundColor: "#1E3A5F",
                color: "#fff",
                "&:hover": { backgroundColor: "#16304E" },
                // Inactive rows read as unavailable rather than as an error —
                // this replaces the old "--" placeholder, which left the
                // column looking ragged.
                "&.Mui-disabled": {
                  backgroundColor: "#F2F4F7",
                  color: "#98A2B3",
                },
                "& .MuiButton-startIcon": { marginRight: "6px" },
              }}
            >
              Create Policy
            </Button>
          </span>
        </Tooltip>
      ),
    }));

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const GetData = () => {
    setLoading(true);
    Promise.all([GetMakeModel(), loadUserAssignments()])
      .then(([res, assignments]) => {
        const rows = assignments || [];
        const restrict = !isMasterAdmin && rows.length > 0;
        // userDiscount by make/model id, so the table shows THIS user's value
        // rather than the shared master one.
        // Assigned ids drive the list filter; the discount override applies
        // only where the user switched it on.
        const assignedIds = new Set(rows.map((r) => String(r.makemodel)));
        const byId = new Map(
          rows
            .filter((r) => r.useOwnDiscount === true)
            .map((r) => [String(r.makemodel), Number(r.Discount)])
        );

        let updatedData = (res?.data || []).map((item) => {
          const ownDiscount = byId.get(String(item._id));
          return {
            ...item,
            vehicleVariant: item.vehicleVariant || "-",
            // Show the user's own discount when they have one assigned.
            discount: Number.isFinite(ownDiscount) ? ownDiscount : item.discount,
            masterDiscount: item.discount,
            isUserDiscount: Number.isFinite(ownDiscount),
            createdAt: moment(item.createdAt).format("DD/MM/YYYY hh:mm A"),
            updatedAt: moment(item.updatedAt).format("DD/MM/YYYY hh:mm A"),
            status: item.isEnabled ? "Active" : "Inactive",
          };
        });

        if (restrict) {
          // Filter on ASSIGNMENT, not on the override — a user still sees a
          // make/model they were given even if they have not set their own
          // discount for it.
          updatedData = updatedData.filter((item) => assignedIds.has(String(item._id)));
        }
        setMakeModel(updatedData);
      })
      .finally(() => setLoading(false));
  };

  const GetActiveData = () => {
    setLoading(true);
    GetMakeModel().then((res) => {
      const updatedData = res?.data.map((item) => ({
        ...item,
        vehicleVariant: item.vehicleVariant || "-",
        createdAt: moment(item.createdAt).format("DD/MM/YYYY hh:mm A"),
        updatedAt: moment(item.updatedAt).format("DD/MM/YYYY hh:mm A"),
        status: item.isEnabled ? "Active" : "Inactive",
      }));
      setMakeModel(updatedData.filter((item) => item.isEnabled === true));
    });
    setLoading(false);
  };

  const GetInActiveData = () => {
    setLoading(true);
    GetMakeModel().then((res) => {
      const updatedData = res?.data.map((item) => ({
        ...item,
        vehicleVariant: item.vehicleVariant || "-",
        createdAt: moment(item.createdAt).format("DD/MM/YYYY hh:mm A"),
        updatedAt: moment(item.updatedAt).format("DD/MM/YYYY hh:mm A"),
        status: item.isEnabled ? "Active" : "Inactive",
      }));
      setMakeModel(updatedData.filter((item) => item.isEnabled === false));
    });
    setLoading(false);
  };

  React.useEffect(() => {
    GetData();
  }, []);

  const EditMakeModelData = (row) => {
    if (assignedOnly) {
      // Restricted users may change only their own discount — never the shared
      // master record.
      setDiscountRow(row);
      setOpenDiscountDialog(true);
      return;
    }
    setSelectedData(row);
    setOpenMakeModelDrawer(true);
  };

  /** Save the edited discount onto the logged-in user's makemodelids. */
  const handleSaveOwnDiscount = async (row) => {
    try {
      const userId = sessionStorage.getItem("UserId");
      if (!userId) {
        ToastError("Could not identify the logged-in user");
        return;
      }

      const current = userMakeModelRows || [];
      const updated = current.map((entry) =>
        String(entry.makemodel) === String(row.makemodel)
          ? {
            makemodel: entry.makemodel,
            Discount: row.useOwnDiscount ? Number(row.Discount) : null,
            useOwnDiscount: !!row.useOwnDiscount,
          }
          : {
            makemodel: entry.makemodel,
            Discount: entry.Discount === null || entry.Discount === undefined
              ? null
              : Number(entry.Discount),
            useOwnDiscount: entry.useOwnDiscount === true,
          }
      );

      // Same transport the user form uses: multipart FormData with the array
      // stringified; dao/userDao.js putUser parses it back.
      const formData = new FormData();
      formData.append("makemodelids", JSON.stringify(updated));

      const res = await UpdateUser(userId, formData);
      if (res && !res.error) {
        ToastSuccess("Discount updated");
        setOpenDiscountDialog(false);
        setDiscountRow(null);
        GetData();
      } else {
        ToastError(res?.message || "Could not update the discount");
      }
    } catch (e) {
      ToastError(e.message || "Could not update the discount");
    }
  };

  const CreateMakeModelData = () => {
    setSelectedData({});
    setOpenMakeModelDrawer(true);
  };

  const ActiveMakeModelData = (row, active) => {
    setOpenConfirmBox(true);
    setSelectedData(row);
    setActiveBoolean(!active);
  };

  const ActiveFunction = (event) => {
    UpdateMakeModel(selectedData._id, { isEnabled: activeBoolean }).then(
      (res) => {
        switch (pageView) {
          case 1:
            GetData();
            break;
          case 2:
            GetActiveData();
            break;
          case 3:
            GetInActiveData();
            break;
          default:
            break;
        }
      }
    );
    setOpenConfirmBox(false);
  };

  const FilterMakeModelFunction = (e) => {
    setPageView(e?.value);
    let data = e?.isEnabled;
    let apiMethod =
      data === "all"
        ? GetData()
        : FilterMakeModel({ isEnabled: data }).then((res) => {
          setMakeModel(res?.data);
          const updatedData = res?.data.map((item) => ({
            ...item,
            vehicleVariant: item.vehicleVariant || "-",
            createdAt: moment(item.createdAt).format("DD/MM/YYYY hh:mm A"),
            updatedAt: moment(item.updatedAt).format("DD/MM/YYYY hh:mm A"),
            status: item.isEnabled ? "Active" : "Inactive",
          }));
          setMakeModel(updatedData);
        });
    setPage(0);
  };

  return (
    <>
      <Grid container className="Master_Card_Container">
        <Grid
          container
          alignItems={"center"}
          className="Master_Card_Sub_Container"
        >
          <Grid item xs={12} sm={6} md={6}>
            <Typography className="Master_Card_Tittle">
              Make and Model
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            className="d-flex justify-content-end"
            sx={{ gap: 1 }}
          >
            {/* CLIENT only, as with Create: this opens the editor for the
                shared IDV formula, and that one record prices every policy. */}
            {isMasterAdmin && (
              <Button
                variant="outlined"
                startIcon={<CalculateIcon />}
                onClick={() => setIdvCalcRow({})}
                sx={{
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  borderColor: "#1E3A5F",
                  color: "#1E3A5F",
                  "&:hover": {
                    borderColor: "#16304E",
                    backgroundColor: "#F2F4F7",
                  },
                }}
              >
                IDV Formula
              </Button>
            )}
            <Button
              className="Master_Card_create_Button w-100"
              startIcon={
                <AddIcon
                  sx={{
                    marginRight: "1.5px",
                  }}
                />
              }
              onClick={() => CreateMakeModelData()}
              // Restricted users cannot add to the shared master.
              style={{ display: assignedOnly ? "none" : undefined }}
            >
              Create
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider
            sx={{
              backgroundColor: "#EAECF0",
              borderWidth: 1,
            }}
          />
        </Grid>
        <Grid
          container
          alignItems={"center"}
          justifyContent={"space-between"}
          className="Master_Card_Sub_Container"
        >
          <TabFilter
            options={FilterOption}
            selectedTab={selectedTab}
            onTabChange={handleTabChange}
          />
          <Grid item xs={12} sm={4} md={3.1} mt={{ xs: 2, sm: 0, md: 0 }}>
            <Input
              focused="false"
              className="w-100 Master_Card_Input"
              id="standard-adornment-password"
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(0);
              }}
              disableUnderline={true}
              placeholder="Search in Make and Model"
              startAdornment={
                <InputAdornment position="start" sx={{ marginRight: "0px" }}>
                  <IconButton aria-label="toggle password visibility">
                    <SearchIcon
                      sx={{
                        fontSize: "20px",
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
        </Grid>
        <MasterCommonTable
          tableConfig={
            assignedOnly
              ? // Restricted users may edit their discount but must not
                // activate/deactivate a shared master record.
                { ...tableConfig, isSwitch: false, isDelete: false }
              : tableConfig
          }
          data={FilteredData}
          columns={headCells}
          handleEdit={EditMakeModelData}
          handleApprove={ActiveMakeModelData}
          tableTotalCount={makeModel?.length || 0}
          loading={loading}
        />
      </Grid>

      <Dialog
        open={openMakeModelDrawer}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="alert-dialog-slide-description"
        PaperProps={{
          sx: {
            maxWidth: {
              xs: "80%",
              sm: "70%",
              md: "50%",
              lg: "40%",
            },
          },
        }}
      >
        {openMakeModelDrawer ? (
          <AddMakeModel
            title={selectedData._id ? "Edit" : "Create"}
            selectedData={selectedData}
            formType={selectedData._id ? "edit" : "add"}
            setOpenMakeModelDrawer={setOpenMakeModelDrawer}
            GetData={GetData}
          />
        ) : null}
      </Dialog>

      {/* Discount-only edit for restricted users. Reuses the drawer built for
          the user page: make/model read-only, discount editable. */}
      {openDiscountDialog && discountRow ? (
        <Dialog
          open={openDiscountDialog}
          onClose={() => setOpenDiscountDialog(false)}
          // index.scss:623 forces .MuiDialog-paper to width:100% !important,
          // so the size has to be capped through maxWidth, not width.
          PaperProps={{
            sx: { maxWidth: "560px !important", overflow: "visible" },
          }}
        >
          <AddMakeModelDrawer
            formType="edit"
            selectedRow={{
              values: {
                makemodel: discountRow._id,
                Discount: discountRow.isUserDiscount ? discountRow.discount : "",
                useOwnDiscount: !!discountRow.isUserDiscount,
                make: discountRow.make,
                model: discountRow.model,
              },
              index: 0,
            }}
            existingRows={userMakeModelRows || []}
            onSave={handleSaveOwnDiscount}
            onClose={() => setOpenDiscountDialog(false)}
          />
        </Dialog>
      ) : null}

      {/* Ex-showroom price -> IDV range, and the editor for the saved rule.
          Only a CLIENT may change it: the formula is one shared record that
          decides the IDV of every policy quoted afterwards. */}
      <IdvCalculatorDialog
        open={!!idvCalcRow}
        onClose={() => setIdvCalcRow(null)}
        row={idvCalcRow}
        canEdit={isMasterAdmin}
      />

      <ConfirmBox
        open={openConfirmBox}
        title={selectedData.isEnabled ? "Inactive" : "Active"}
        content={`Are you sure want to ${selectedData.isEnabled ? "Inactive" : "Active"
          }`}
        confirmButton={selectedData.isEnabled ? "Inactive" : "Active"}
        setOpenConfirmBox={setOpenConfirmBox}
        Function={ActiveFunction}
        img={selectedData.isEnabled ? DisabledImage : ActiveImage}
        icon={
          selectedData.isEnabled ? <NotInterestedIcon /> : <DoneOutlineIcon />
        }
        color={selectedData.isEnabled ? "error" : "success"}
      />
    </>
  );
};

export default MakeModel;
